/**
 * `<gh-example-bar>` - "you are looking at the example", always in view, with the way out
 * (`services/exampleWorkspace.ts`; owner ask 2026-09-24: *"a 'See example' feature … Should be
 * readonly"*).
 *
 * The claim card's two shapes and its two slots (`app.ts`): `sidebar` where a signed-in account shows
 * its email and Sign out, and `bar` in the phone's top bar, where the sidebar is a drawer nobody sees
 * until they open it. The example has no email and no sign-out - the visitor never signed in as it -
 * so the slot is free, and it is the one place on every page that can say what this workspace is.
 *
 * ⚠ **The way out depends on who is looking.** Somebody with an account of their own goes back to it
 * (`/example/exit` lands on `/`, which is their workspace). A stranger is offered the product itself:
 * "Get started" is the landing page's own form when guest accounts are on, and the sign-up screen
 * when they are not - reached THROUGH `/example/exit`, because the example cookie would otherwise
 * boot the sign-up page straight back into the example.
 */
import { css, html, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { GhElement } from "./ghElement.ts";
import { buttonStyles, chromeStyles, focusStyles, resetStyles, tooltipStyles } from "./sharedStyles.ts";
import { t } from "../i18n.ts";

@customElement("gh-example-bar")
export class GhExampleBar extends GhElement {
	@property() accessor variant: "sidebar" | "bar" = "sidebar";
	/** The visitor has an account of their own to go back to */
	@property({ type: Boolean }) accessor signedIn = false;
	/** "Get started" makes a guest workspace (`GUEST_ACCOUNTS`), rather than opening the sign-up form */
	@property({ type: Boolean }) accessor guestStart = false;

	static styles = [
		resetStyles,
		chromeStyles,
		buttonStyles,
		tooltipStyles,
		focusStyles,
		css`
			:host {
				display: block;
			}
			.example-card {
				margin: 14px 12px 0;
				padding: 12px;
				border: 1px solid var(--chrome-border);
				border-radius: var(--radius);
				background: var(--chrome-bg3);
				color: var(--chrome-text);
				display: flex;
				flex-direction: column;
				gap: 10px;
			}
			.example-state {
				display: flex;
				align-items: center;
				gap: 8px;
				font-size: 0.8125rem;
				font-weight: 600;
			}
			.example-state .tooltip-icon {
				margin-left: auto;
			}
			/* Steady, not pulsing: unlike the claim card's dot this marks nothing at risk - it is a label. */
			.example-dot {
				width: 8px;
				height: 8px;
				border-radius: 50%;
				flex: none;
				background: var(--accent-2);
			}
			.example-card .btn,
			.example-card form {
				width: 100%;
			}
			.example-card .btn {
				justify-content: center;
			}
			.example-leave {
				align-self: flex-start;
				color: var(--chrome-text2);
				font-size: 0.75rem;
			}
			.example-bar {
				display: flex;
				align-items: center;
				gap: 8px;
				justify-self: end;
			}
			.example-bar .btn {
				white-space: nowrap;
			}
			/* The phone's top bar is the PAGE's surface, not the ink sidebar's, so the chip reads the page's
			   text token - the sidebar card's --chrome-text is a light ink color that vanishes here. */
			.example-chip {
				font-size: 0.75rem;
				font-weight: 600;
				padding: 2px 8px;
				border-radius: 999px;
				background: color-mix(in srgb, var(--accent-2) 18%, transparent);
				color: var(--text);
				white-space: nowrap;
			}
		`
	];

	/** The one action this card leads with, in the shape the visitor needs */
	#primary(small: boolean): TemplateResult {
		const size = small ? " btn-small" : "";
		if (this.signedIn) {
			return html`<a class="btn btn-primary${size}" href="/example/exit" data-action="example-back"
				>${t(small ? "example.backShort" : "example.back")}</a
			>`;
		}
		if (this.guestStart) {
			// The landing page's own button: a plain form POST, which `/api/auth/guest` checks came from us.
			return html`<form method="post" action="/api/auth/guest">
				<button type="submit" class="btn btn-primary${size}" data-action="example-start">${t("example.getStarted")}</button>
			</form>`;
		}
		return html`<a class="btn btn-primary${size}" href="/example/exit?to=signup" data-action="example-start">${t("example.getStarted")}</a>`;
	}

	protected render(): TemplateResult {
		if (this.variant === "bar") {
			return html`<div class="example-bar">
				<span class="example-chip">${t("example.chip")}</span>
				${this.#primary(true)}
			</div>`;
		}
		return html`<div class="example-card">
			<p class="example-state">
				<span class="example-dot" aria-hidden="true"></span>${t("example.cardState")}
				<span class="tooltip-icon" tabindex="0" data-tooltip=${t("example.cardTip")}>?</span>
			</p>
			${this.#primary(false)}
			${this.signedIn ? null : html`<a class="example-leave" href="/example/exit" data-action="example-leave">${t("example.leave")}</a>`}
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"gh-example-bar": GhExampleBar;
	}
}
